import React, { useMemo, useEffect, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { useParams, useOutletContext } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector, Tooltip } from "recharts";
import "./Segmentation.css";
import { useLotAnalytics, useLotHourlyData } from "../../hooks/useLotData";
import { useMultiLot } from "../../contexts/MultiLotContext";
import { useMultiLotAnalytics, useMultiLotHourlyData } from "../../hooks/useMultiLotData";
import { parseDateAsUTC } from "../../utils/dateUtils";

const COLORS = ["var(--parallel-blue)", "var(--graph-yellow)", "var(--graph-cyan)"];

function getOrdinal(n: number) {
    const j = n % 10, k = n % 100;
    if (j === 1 && k !== 11) return n + "st";
    if (j === 2 && k !== 12) return n + "nd";
    if (j === 3 && k !== 13) return n + "rd";
    return n + "th";
}

function formatDateRange(timeframe: string, currentRange: any) {
    const start = parseDateAsUTC(currentRange.startDate);
    const end = parseDateAsUTC(currentRange.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (timeframe === 'day') {
        if (start.getTime() === today.getTime()) return 'today';
        const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
        if (start.getTime() === yesterday.getTime()) return 'yesterday';
        return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (timeframe === 'week') {
        // Check if current week
        const weekStart = new Date(today); weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
        if (start.getTime() === weekStart.getTime() && end.getTime() === weekEnd.getTime()) return 'this week';
        // Format as range
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (timeframe === 'month') {
        if (start.getMonth() === today.getMonth() && start.getFullYear() === today.getFullYear()) return 'this month';
        return `${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    }
    if (timeframe === 'year') {
        if (start.getFullYear() === today.getFullYear()) return 'this year';
        return `in ${start.getFullYear()}`;
    }
    return '';
}

function getViolatorTitle(timeframe: string, currentRange: any) {
    const label = formatDateRange(timeframe, currentRange);
    if (label === 'today') return 'Violators Today';
    if (label === 'yesterday') return 'Violators Yesterday';
    if (label === 'this week') return 'Violators this Week';
    if (label === 'this month') return 'Violators this Month';
    if (label === 'this year') return 'Violators this Year';
    if (timeframe === 'week') return `Violators (${label})`;
    if (timeframe === 'month') return `Violators in ${label}`;
    if (timeframe === 'year') return `Violators ${label}`;
    return `Violators on ${label}`;
}

function getViolatorTagline(timeframe: string, currentRange: any, count: number) {
    const label = formatDateRange(timeframe, currentRange);
    if (label === 'today') return `${count} violations were distributed today.`;
    if (label === 'yesterday') return `${count} violations were distributed yesterday.`;
    if (label === 'this week') return `${count} violations were distributed this week.`;
    if (label === 'this month') return `${count} violations were distributed this month.`;
    if (label === 'this year') return `${count} violations were distributed this year.`;
    if (timeframe === 'week') return `${count} violations were distributed ${label}.`;
    if (timeframe === 'month') return `${count} violations were distributed in ${label}.`;
    if (timeframe === 'year') return `${count} violations were distributed ${label}.`;
    return `${count} violations were distributed on ${label}.`;
}

function getPrevLabel(timeframe: string) {
    if (timeframe === 'day') return 'Previous Day';
    if (timeframe === 'week') return 'Previous Week';
    if (timeframe === 'month') return 'Previous Month';
    if (timeframe === 'year') return 'Previous Year';
    return 'Previous Period';
}

function percentChange(current: number, prev: number) {
    if (prev === 0) return current === 0 ? 0 : 100;
    return ((current - prev) / Math.abs(prev)) * 100;
}

function darkenColor(color: string, percent: number): string {
    const num = parseInt(color.slice(1), 16);
    let r = (num >> 16) & 0xFF;
    let g = (num >> 8) & 0xFF;
    let b = num & 0xFF;
    r = Math.floor(r * (1 - percent / 100));
    g = Math.floor(g * (1 - percent / 100));
    b = Math.floor(b * (1 - percent / 100));
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
}

const renderActiveSector = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    // Use a subtle scale transform for hover effect
    return (
        <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius * 1.02}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
        />
    );
};

const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="occupancy-tooltip-box">
                <div className="occupancy-tooltip-line1">{data.name}</div>
                <div className="occupancy-tooltip-line2">{data.value}</div>
            </div>
        );
    }
    return null;
};

const Segmentation: React.FC = () => {
    const { lotId } = useParams<{ lotId: string }>();
    const { timeframe, currentRange, previousRange } = useOutletContext<any>();
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    // Multi-lot context
    const { isMultiLotMode, getSelectedLotIds } = useMultiLot();
    const selectedLotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ""];

    // Single-lot hooks (always called to maintain hook order)
    const singleLotCurrentAnalytics = useLotAnalytics(
        lotId || "",
        currentRange.startDate,
        currentRange.endDate,
        timeframe
    );

    const singleLotPreviousAnalytics = useLotAnalytics(
        lotId || "",
        previousRange.startDate,
        previousRange.endDate,
        timeframe
    );

    const singleLotHourlyData = useLotHourlyData(
        lotId || "",
        currentRange.startDate,
        timeframe
    );

    // Multi-lot hooks (always called to maintain hook order)
    const multiLotCurrentAnalytics = useMultiLotAnalytics(
        selectedLotIds,
        currentRange.startDate,
        currentRange.endDate,
        timeframe
    );

    const multiLotPreviousAnalytics = useMultiLotAnalytics(
        selectedLotIds,
        previousRange.startDate,
        previousRange.endDate,
        timeframe
    );

    const multiLotHourlyData = useMultiLotHourlyData(
        selectedLotIds,
        currentRange.startDate,
        timeframe
    );

    // Select appropriate data based on mode
    const currentMetricsData = isMultiLotMode ? multiLotCurrentAnalytics.data : singleLotCurrentAnalytics.data;
    const previousMetricsData = isMultiLotMode ? multiLotPreviousAnalytics.data : singleLotPreviousAnalytics.data;
    const hourlyDataData = isMultiLotMode ? multiLotHourlyData.data : singleLotHourlyData.data;

    const isCurrentLoading = isMultiLotMode ? multiLotCurrentAnalytics.isLoading : singleLotCurrentAnalytics.isLoading;
    const isCurrentFetching = isMultiLotMode ? multiLotCurrentAnalytics.isFetching : singleLotCurrentAnalytics.isFetching;
    const isPreviousLoading = isMultiLotMode ? multiLotPreviousAnalytics.isLoading : singleLotPreviousAnalytics.isLoading;
    const isPreviousFetching = isMultiLotMode ? multiLotPreviousAnalytics.isFetching : singleLotPreviousAnalytics.isFetching;
    const isHourlyLoading = isMultiLotMode ? multiLotHourlyData.isLoading : singleLotHourlyData.isLoading;
    const isHourlyFetching = isMultiLotMode ? multiLotHourlyData.isFetching : singleLotHourlyData.isFetching;

    const onPieEnter = (_: any, index: number) => {
        setActiveIndex(index);
    };

    // Use hourly data if available and it's today
    const effectiveCurrentData = useMemo(() => {
        if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
            return hourlyDataData.data;
        }
        return currentMetricsData;
    }, [timeframe, hourlyDataData, currentMetricsData]);

    // Aggregate data for the current view
    const metrics = useMemo(() => {
        if (!effectiveCurrentData || effectiveCurrentData.length === 0) return null;
        let totalRegistered = 0, totalReturn = 0, totalNew = 0, totalViolations = 0, totalVehicles = 0;

        if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
            // For hourly data, sum up all values
            hourlyDataData.data.forEach((entry: any) => {
                totalRegistered += Number(entry.totalRegisteredVehicles || 0);
                totalReturn += Number(entry.totalUnregisteredReturnVehicles || 0);
                totalNew += Number(entry.totalNewVehicles || 0);
                totalViolations += Number(entry.totalViolations || 0);
                totalVehicles += Number(entry.totalVehicles || 0);
            });
        } else {
            // For other timeframes, use daily data
            effectiveCurrentData.forEach((entry: any) => {
                totalRegistered += Number(entry.totalRegisteredVehicles || 0);
                totalReturn += Number(entry.totalUnregisteredReturnVehicles || 0);
                totalNew += Number(entry.totalNewVehicles || 0);
                totalViolations += Number(entry.totalViolations || 0);
                totalVehicles += Number(entry.totalVehicles || 0);
            });
        }

        return {
            totalRegistered,
            totalReturn,
            totalNew,
            totalViolations,
            totalVehicles
        };
    }, [effectiveCurrentData, timeframe, hourlyDataData]);

    // Aggregate data for previous view
    const prevMetrics = useMemo(() => {
        if (!previousMetricsData || previousMetricsData.length === 0) return null;
        let totalRegistered = 0, totalReturn = 0, totalNew = 0, totalViolations = 0, totalVehicles = 0;

        previousMetricsData.forEach((entry: any) => {
            totalRegistered += Number(entry.totalRegisteredVehicles || 0);
            totalReturn += Number(entry.totalUnregisteredReturnVehicles || 0);
            totalNew += Number(entry.totalNewVehicles || 0);
            totalViolations += Number(entry.totalViolations || 0);
            totalVehicles += Number(entry.totalVehicles || 0);
        });

        return {
            totalRegistered,
            totalReturn,
            totalNew,
            totalViolations,
            totalVehicles
        };
    }, [previousMetricsData]);

    // Pie chart data
    const pieData = useMemo(() => metrics ? [
        { name: "Registered Vehicles", value: metrics.totalRegistered },
        { name: "Return Visitors", value: metrics.totalReturn },
        { name: "New Visitors", value: metrics.totalNew },
    ] : [], [metrics]);

    // Violator percentage
    const violatorPercent = metrics && metrics.totalVehicles > 0 ? (metrics.totalViolations / metrics.totalVehicles) * 100 : 0;
    const prevViolatorPercent = prevMetrics && prevMetrics.totalVehicles > 0 ? (prevMetrics.totalViolations / prevMetrics.totalVehicles) * 100 : 0;

    // Animated values using framer-motion springs
    const animatedRegistered = useSpring(metrics ? metrics.totalRegistered : 0, { stiffness: 10000, damping: 600 });
    const animatedReturn = useSpring(metrics ? metrics.totalReturn : 0, { stiffness: 10000, damping: 600 });
    const animatedNew = useSpring(metrics ? metrics.totalNew : 0, { stiffness: 10000, damping: 600 });
    const animatedViolations = useSpring(metrics ? metrics.totalViolations : 0, { stiffness: 10000, damping: 600 });
    const animatedViolatorPct = useSpring(violatorPercent, { stiffness: 10000, damping: 600 });

    useEffect(() => {
        if (metrics) {
            animatedRegistered.set(metrics.totalRegistered);
            animatedReturn.set(metrics.totalReturn);
            animatedNew.set(metrics.totalNew);
            animatedViolations.set(metrics.totalViolations);
        }
    }, [metrics]);

    useEffect(() => {
        animatedViolatorPct.set(violatorPercent);
    }, [violatorPercent]);

    const displayRegistered = useTransform(animatedRegistered, (v: number) => Math.round(v));
    const displayReturn = useTransform(animatedReturn, (v: number) => Math.round(v));
    const displayNew = useTransform(animatedNew, (v: number) => Math.round(v));
    const displayViolations = useTransform(animatedViolations, (v: number) => Math.round(v));
    const displayViolatorPct = useTransform(animatedViolatorPct, (v: number) => v.toFixed(1));

    // Tagline logic
    let tagline = "";
    if (metrics) {
        const max = Math.max(metrics.totalRegistered, metrics.totalReturn, metrics.totalNew);
        if (max === metrics.totalRegistered) tagline = "Most of your visitors are permit holders.";
        else if (max === metrics.totalReturn) tagline = "Most of your visitors are return guests.";
        else tagline = "Most of your visitors are visiting for the first time.";
    }

    const hasSegmentationData =
        (effectiveCurrentData && effectiveCurrentData.length > 0) ||
        (timeframe === "day" && hourlyDataData?.data && hourlyDataData.data.length > 0);

    const isLoading =
        isCurrentLoading ||
        isPreviousLoading ||
        isHourlyLoading ||
        isCurrentFetching ||
        isPreviousFetching ||
        isHourlyFetching ||
        !metrics ||
        !hasSegmentationData;

    if (isLoading) {
        return (
            <div className="segmentation-content segmentation-skeleton">
                <div className="segmentation-skeleton-top">
                    <div className="skeleton-block segmentation-skeleton-main" aria-hidden="true" />
                </div>
                <div className="segmentation-skeleton-bottom">
                    <div className="skeleton-block segmentation-skeleton-bottom-single" aria-hidden="true" />
                </div>
            </div>
        );
    }

    // Helper for up/down color
    const getChangeClass = (val: number) => val > 0 ? "change-up" : val < 0 ? "change-down" : "change-same";
    const getChangeSign = (val: number) => val > 0 ? "+" : val < 0 ? "-" : "";

    // Calculate changes
    const regChange = prevMetrics ? percentChange(metrics.totalRegistered, prevMetrics.totalRegistered) : 0;
    const retChange = prevMetrics ? percentChange(metrics.totalReturn, prevMetrics.totalReturn) : 0;
    const newChange = prevMetrics ? percentChange(metrics.totalNew, prevMetrics.totalNew) : 0;
    const violChange = prevMetrics ? percentChange(metrics.totalViolations, prevMetrics.totalViolations) : 0;
    const violPctChange = prevMetrics ? percentChange(violatorPercent, prevViolatorPercent) : 0;
    const prevLabel = getPrevLabel(timeframe);

    return (
        <div className="segmentation-content">
            <div className="segmentation-main-box">
                <div className="segmentation-pie-container">
                    <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                            <Tooltip content={<CustomPieTooltip />} />
                            <Pie
                                key={timeframe}
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={70}
                                outerRadius={130}
                                paddingAngle={2}
                                dataKey="value"
                                nameKey="name"
                                activeIndex={activeIndex}
                                activeShape={renderActiveSector}
                                onMouseEnter={onPieEnter}
                                onMouseLeave={() => setActiveIndex(-1)}
                                stroke="none"
                            >
                                {pieData.map((entry, idx) => (
                                    <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="segmentation-legend">
                    <div className="legend-title">Segmentation</div>
                    <div className="legend-desc">{tagline}</div>
                    <ul className="legend-list">
                        <li style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span className="legend-dot" style={{ background: COLORS[0] }}></span>
                                <span style={{ marginLeft: '8px' }}>
                                    <b style={{ fontWeight: '600' }}><motion.span>{displayRegistered}</motion.span></b> Registered Vehicles
                                </span>
                            </div>
                            <div>
                                <motion.span key={`reg-change-${regChange}`} className={`legend-change ${getChangeClass(regChange)}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}>
                                    {getChangeSign(regChange)}{Math.abs(regChange).toFixed(1)}% {prevLabel}
                                </motion.span>
                            </div>
                        </li>
                        <li style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span className="legend-dot" style={{ background: COLORS[1] }}></span>
                                <span style={{ marginLeft: '8px' }}>
                                    <b style={{ fontWeight: '600' }}><motion.span>{displayReturn}</motion.span></b> Return Visitors
                                </span>
                            </div>
                            <div>
                                <motion.span key={`ret-change-${retChange}`} className={`legend-change ${getChangeClass(retChange)}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}>
                                    {getChangeSign(retChange)}{Math.abs(retChange).toFixed(1)}% {prevLabel}
                                </motion.span>
                            </div>
                        </li>
                        <li style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span className="legend-dot" style={{ background: COLORS[2] }}></span>
                                <span style={{ marginLeft: '8px' }}>
                                    <b style={{ fontWeight: '600' }}><motion.span>{displayNew}</motion.span></b> New Visitors
                                </span>
                            </div>
                            <div>
                                <motion.span key={`new-change-${newChange}`} className={`legend-change ${getChangeClass(newChange)}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}>
                                    {getChangeSign(newChange)}{Math.abs(newChange).toFixed(1)}% {prevLabel}
                                </motion.span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="segmentation-violator-box">
                <div className="violator-metric">
                    <div className="violator-title">{getViolatorTitle(timeframe, currentRange)}</div>
                    <div className="violator-value"><motion.span>{displayViolations}</motion.span></div>
                    <div className="violator-tagline">{getViolatorTagline(timeframe, currentRange, metrics.totalViolations)}</div>
                    <motion.span key={`viol-change-${violChange}`} className={`violator-change ${getChangeClass(violChange)}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}>
                        {getChangeSign(violChange)}{Math.abs(violChange).toFixed(1)}% {prevLabel}
                    </motion.span>
                </div>
                <div className="violator-percentage-metric">
                    <div className="violator-percentage-title">Violation Percentage</div>
                    <div className="violator-percentage-value"><motion.span>{displayViolatorPct}</motion.span>%</div>
                    <div className="violator-bar-bg">
                        <div className="violator-bar-fill" style={{ width: `${violatorPercent}%` }}></div>
                    </div>
                    <motion.span key={`violpct-change-${violPctChange}`} className={`violator-change ${getChangeClass(violPctChange)}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}>
                        {getChangeSign(violPctChange)}{Math.abs(violPctChange).toFixed(1)}% {prevLabel}
                    </motion.span>
                </div>
            </div>
        </div>
    );
};

export default Segmentation;
